import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  FindControlAdditionalSearchParametersDto,
  FindControlColumnConfigItemDto,
  FindControlDeleteItemResultDto,
  FindControlInitDto,
  FindControlResultDto,
  FindControlUIBehaviorDto,
  FindControlUserColumnsConfigDto,
  GetDataSearchDto,
  GetMfuSearchDto,
  GetMruSearchDto,
  IColumnPk,
} from 'webcommon/shared';
import { FindControlRepository } from 'webcommon/webapi';
import { FindControlCustomData } from './FindControlCustomData';

@Injectable({
  providedIn: 'root',
})
export class ApFindControlService {

  constructor(private findControlRepository: FindControlRepository) { }

  exportAll(behaviorDto: FindControlUIBehaviorDto) {
    return this.findControlRepository.exportAll(behaviorDto.Id, {
      IncludeSystemItems: true,
    });
  }

  exportItems(itemIds: string[], behaviorDto: FindControlUIBehaviorDto) {
    return this.findControlRepository.exportItems(behaviorDto.Id, {
      IncludeSystemItems: true,
      ItemIds: itemIds,
    });
  }

  importItems(xml: string, behaviorDto: FindControlUIBehaviorDto) {
    return this.findControlRepository.importItems(behaviorDto.Id, {
      Xml: xml,
    });
  }

  getConfig(id: string): Observable<FindControlInitDto> {
    return this.findControlRepository.getConfig(id);
  }

  getData(
    id: string,
    searchDto: GetDataSearchDto,
    userSearchParameters?: FindControlAdditionalSearchParametersDto,
  ): Observable<FindControlResultDto> {
    // TODO: userSearchParameters need to be merged into the dto instead of replacing
    if (userSearchParameters) {
      searchDto = { ...searchDto };
      searchDto.SearchText = '';
      searchDto.AdditionalSearchParameters = userSearchParameters;
    }
    return this.findControlRepository.getData(id, searchDto);
  }

  getCustomData(searchDto: GetDataSearchDto, customData?: FindControlCustomData): FindControlResultDto {
    if (!customData || ((searchDto.SearchText === '') && !searchDto.Prepopulate)) {
      return {
        AdditionalRows: false,
        Rows: [],
      };
    }
    if (searchDto.SearchText === '' || searchDto.SearchText === '%') {
        return customData.Items;
    }
    const filterByColumns = this.getFilterableColumnNames(customData.Config.FindControlBehavior.ColumnsConfig.Columns);
    const filterResults = this.filterObjectsBySearchText(customData.Items.Rows, searchDto.SearchText, filterByColumns);
    return {
      AdditionalRows: false,
      Rows: filterResults,
    };
  }

  getSelectedItems(id: string, guids: string[]): Observable<FindControlResultDto> {
    return this.findControlRepository.getSelectedItems(id, guids);
  }

  getCustomSelectedItems(guids: string[], customData?: FindControlCustomData): FindControlResultDto {
    const temp =  {
      AdditionalRows: false,
      Rows: customData ? this.filterItemsByPk(customData.Items.Rows, guids) : [],
    };
    return temp;
  }

  getItem<T = FindControlResultDto>(id: string, itemPk: string): Observable<T> {
    return this.findControlRepository.getItem(id, itemPk);
  }

  getMruList(id: string, maxrows: number, searchDto: GetMruSearchDto): Observable<FindControlResultDto> {
    return this.findControlRepository.getMruList(id, maxrows, searchDto);
  }

  getMfuList(id: string, maxrows: number, searchDto: GetMfuSearchDto): Observable<FindControlResultDto> {
    return this.findControlRepository.getMfuList(id, maxrows, searchDto);
  }

  updateMfuMruList(id: string, guids: string[]): Observable<string[]> {
    return this.findControlRepository.updateMfuMruList(id, guids);
  }

  deleteItem(id: string, controlId: string): Observable<FindControlDeleteItemResultDto> {
    return this.findControlRepository.deleteItem(id, controlId);
  }

  canDeleteItem(id: string, controlId: string): Observable<FindControlDeleteItemResultDto> {
    return this.findControlRepository.canDeleteItem(id, controlId);
  }

  mapFindControlObject<T extends object>(object: IColumnPk): T | null {
    const newObject: T | null = {} as T;
    Object.assign(newObject, object);

    return newObject;
  }

  mergeItem(id: string, controlId: string, newId: string): Observable<FindControlDeleteItemResultDto> {
    return this.findControlRepository.mergeItem(id, controlId, newId);
  }

  updateUserColumnsConfig(config: FindControlUserColumnsConfigDto, behaviorDto: FindControlUIBehaviorDto) {
    return this.findControlRepository.updateUserColumnsConfig(behaviorDto.Id, config);
  }

  private filterItemsByPk(items: IColumnPk[], guids: string[]): IColumnPk[] {
    return items.filter(item => guids.includes(item.Pk));
  }

  private getFilterableColumnNames(configColumns: Array<FindControlColumnConfigItemDto<any>>): string[] {
    return configColumns
        .filter(column => column.CanFilter)
        .map(column => column.ColumnName);
  }

  private filterObjectsBySearchText(dataRows: IColumnPk[], searchText: string, filterByColumns: string[]): IColumnPk[] {
    return dataRows.filter(obj =>
        filterByColumns.some(column =>
            String(obj[column]).toLowerCase().includes(searchText.toLowerCase())
        )
    );
  }
}
